import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  MessageCircle, X, Send, Star, ThumbsUp,
  Loader, ChevronDown, Minimize2, HelpCircle
} from '../ui/icons';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { usePageHelp } from '../../context/PageHelpProvider';
import { PAGE_HELP, resolvePageId } from '../../config/pageHelpContext';

/* ── Generic quick-prompt suggestions ─────────────────────────────────────
   Shown when the current page has no page-specific suggestions in the Page
   Context Map (client/src/config/pageHelpContext.js). Page-aware suggestions
   take precedence when available. */
const FAQ_SUGGESTIONS = [
  { label: 'How do I upload photos?',      text: 'How do I upload photos to my project?' },
  { label: 'What are animated templates?', text: 'What is the difference between slideshow and animated templates?' },
  { label: 'How does sharing work?',       text: 'How do I share my video with family members?' },
  { label: 'How long does it take?',       text: 'How long does video generation take?' },
];

/* ── Proactive tip session tracking ────────────────────────────────────────
   Tips show at most once per page per browser-tab session. sessionStorage
   persists across reloads in the same tab and resets on a new tab/session. */
const TIPS_SEEN_KEY = 'sf_help_tips_seen';

const readSeenTips = () => {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(TIPS_SEEN_KEY) || '[]'));
  } catch (_) {
    return new Set();
  }
};

const markTipSeen = (pageId) => {
  try {
    const seen = readSeenTips();
    seen.add(pageId);
    sessionStorage.setItem(TIPS_SEEN_KEY, JSON.stringify([...seen]));
  } catch (_) { /* sessionStorage unavailable — tip simply may reshow */ }
};

/* ── Message bubble ───────────────────────────────────────────────────── */
const MessageBubble = ({ message }) => {
  const isUser = message.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-brand-500/20 border border-brand-500/30 flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">
          <MessageCircle className="w-3.5 h-3.5 text-brand-400" />
        </div>
      )}
      <div
        className={`max-w-[80%] px-3 py-2 rounded-2xl text-body leading-relaxed whitespace-pre-wrap ${
          isUser
            ? 'bg-brand-500 text-dark-900 font-medium rounded-br-sm'
            : 'bg-dark-600 text-brand-ink rounded-bl-sm border border-dark-500'
        }`}
      >
        {message.content}
      </div>
    </div>
  );
};

/* ── Feedback panel ───────────────────────────────────────────────────── */
const FeedbackPanel = ({ sessionId, onClose }) => {
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [text, setText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!rating && !text.trim()) return;
    setLoading(true);
    try {
      await api.post(`/chat/session/${sessionId}/feedback`, { rating: rating || undefined, text: text.trim() || undefined });
      setSubmitted(true);
    } catch (_) {}
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="p-4 text-center">
        <ThumbsUp className="w-8 h-8 text-brand-400 mx-auto mb-2" />
        <p className="text-brand-ink font-medium text-label">Thank you for your feedback!</p>
        <p className="text-secondary text-label mt-1">Your input helps us improve Mixtape Memories.</p>
        <button onClick={onClose} className="mt-3 text-label text-brand-400 hover:text-brand-300">Close</button>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3">
      <p className="text-brand-ink text-label font-medium">How was your experience?</p>

      {/* Star rating */}
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(n => (
          <button
            key={n}
            type="button"
            onClick={() => setRating(n)}
            onMouseEnter={() => setHovered(n)}
            onMouseLeave={() => setHovered(0)}
            aria-label={`Rate ${n} star${n > 1 ? 's' : ''}`}
            aria-pressed={n <= rating}
            className="min-w-tap min-h-tap flex items-center justify-center rounded-full"
          >
            <Star
              aria-hidden="true"
              className={`w-6 h-6 transition-colors ${
                n <= (hovered || rating) ? 'text-yellow-400 fill-yellow-400' : 'text-dark-400'
              }`}
            />
          </button>
        ))}
      </div>

      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="Any comments or suggestions? (optional)"
        rows={3}
        className="w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-xl text-brand-ink text-body placeholder-brand-taupe focus:outline-none focus:border-brand-500 resize-none"
      />

      <div className="flex gap-2">
        <button
          onClick={handleSubmit}
          disabled={loading || (!rating && !text.trim())}
          className="flex-1 py-2 bg-brand-500 hover:bg-brand-400 text-dark-900 text-label font-semibold rounded-xl transition-colors disabled:opacity-40"
        >
          {loading ? <Loader className="w-3.5 h-3.5 animate-spin mx-auto" /> : 'Submit Feedback'}
        </button>
        <button
          onClick={onClose}
          className="px-3 py-2 bg-dark-600 hover:bg-dark-500 text-secondary text-label rounded-xl transition-colors"
        >
          Skip
        </button>
      </div>
    </div>
  );
};

/* ── Main ChatWidget ──────────────────────────────────────────────────── */

/**
 * Props:
 *  projectId  {string|null}  – current project (if in editor)
 *  step       {string|null}  – current editor step ('photos', 'template', etc.)
 *  photoCount {number}       – how many photos are in the project
 */
const ChatWidget = ({ projectId = null, step = null, photoCount = 0 }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const { editorStep } = usePageHelp();
  const [open, setOpen] = useState(false);
  const [minimised, setMinimised] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [tip, setTip] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Page-aware help. Everything is defensive: if the map/context ever fails,
  // pageId is null and the widget behaves exactly like a generic chat.
  let pageId = null;
  try {
    pageId = resolvePageId(location.pathname, editorStep);
  } catch (_) {
    pageId = null;
  }
  const pageHelp = (pageId && PAGE_HELP[pageId]) || null;
  const proactiveTip = pageHelp?.proactiveTip || null;
  const pageSuggestions =
    pageHelp?.suggestedQuestions?.length ? pageHelp.suggestedQuestions : null;

  // Show a one-time proactive tip when landing on a page that has one and it
  // hasn't been seen this session. Marked seen on show so it never repeats;
  // hidden while the chat is open.
  useEffect(() => {
    if (open) { setTip(null); return; }
    try {
      if (pageId && proactiveTip && !readSeenTips().has(pageId)) {
        setTip(proactiveTip);
        markTipSeen(pageId);
      } else {
        setTip(null);
      }
    } catch (_) {
      setTip(null);
    }
  }, [pageId, proactiveTip, open]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (open && !minimised) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open, minimised]);

  // Focus input when opening
  useEffect(() => {
    if (open && !minimised) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, minimised]);

  // Start a session when widget opens for the first time
  const startSession = useCallback(async () => {
    if (sessionId) return;
    try {
      const { data } = await api.post('/chat/session', {
        projectId: projectId || undefined,
        step: step || undefined,
        photoCount,
        pageId: pageId ?? null
      });
      setSessionId(data.sessionId);

      // Greeting message
      setMessages([{
        role: 'assistant',
        content: `Hello, I'm the Mixtape Memories assistant. I can help you create a memorial video, answer questions about the platform, or pass along any feedback. What would you like to do?`
      }]);
    } catch (_) {
      setMessages([{
        role: 'assistant',
        content: "Hello. I'm here to help. I'm having trouble connecting right now; please refresh if it continues."
      }]);
    }
  }, [sessionId, projectId, step, photoCount, pageId]);

  const handleOpen = () => {
    setOpen(true);
    setMinimised(false);
    setUnreadCount(0);
    startSession();
  };

  const handleClose = () => {
    // Offer feedback if there were real user messages
    const hasUserMessages = messages.some(m => m.role === 'user');
    if (hasUserMessages && !showFeedback) {
      setShowFeedback(true);
      return;
    }
    setOpen(false);
    setShowFeedback(false);
  };

  const sendMessage = async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || loading || !sessionId) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: trimmed }]);
    setLoading(true);

    try {
      const { data } = await api.post(`/chat/session/${sessionId}/message`, {
        message: trimmed,
        step: step || undefined,
        photoCount,
        pageId: pageId ?? null
      });

      setMessages(prev => [...prev, { role: 'assistant', content: data.message }]);
      if (!open || minimised) setUnreadCount(c => c + 1);
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I had trouble responding. Please try again.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Don't render for unauthenticated users
  if (!isAuthenticated) return null;

  return (
    <>
      {/* Proactive page tip: small, dismissible, non-blocking. Sits above the
          floating button; opening the chat or dismissing hides it. */}
      {!open && tip && (
        <div className="fixed bottom-24 right-6 z-50 max-w-[16rem] animate-fade-in">
          <div className="relative bg-dark-800 border border-dark-500 rounded-2xl rounded-br-sm shadow-2xl p-3 pr-7">
            <button
              type="button"
              onClick={() => setTip(null)}
              className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full hover:bg-dark-600 flex items-center justify-center text-secondary"
              aria-label="Dismiss tip"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => { setTip(null); handleOpen(); }}
              className="text-left text-brand-ink text-label leading-relaxed"
            >
              {tip}
            </button>
          </div>
        </div>
      )}

      {/* Floating button */}
      {!open && (
        <button
          onClick={handleOpen}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-brand-500 hover:bg-brand-400 rounded-full shadow-2xl shadow-brand-500/30 flex items-center justify-center transition-all duration-200 hover:scale-110 group"
          aria-label="Open chat assistant"
        >
          <MessageCircle className="w-6 h-6 text-dark-900" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-brand-ink text-2xs font-bold rounded-full flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className={`fixed bottom-6 right-6 z-50 w-80 sm:w-96 bg-dark-800 rounded-2xl border border-dark-500 shadow-2xl flex flex-col transition-all duration-200 ${
            minimised ? 'h-14' : 'h-[520px] max-h-[80vh]'
          }`}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-dark-600 flex-shrink-0">
            <div className="w-8 h-8 rounded-full bg-brand-500/20 border border-brand-500/30 flex items-center justify-center">
              <MessageCircle className="w-4 h-4 text-brand-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-brand-ink text-label font-semibold leading-tight">Mixtape Memories Assistant</p>
              <p className="text-secondary text-label">Powered by Claude AI</p>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setMinimised(m => !m)}
                className="w-7 h-7 rounded-lg hover:bg-dark-600 flex items-center justify-center transition-colors"
                aria-label={minimised ? 'Expand' : 'Minimise'}
              >
                {minimised ? <ChevronDown className="w-4 h-4 text-secondary" /> : <Minimize2 className="w-4 h-4 text-secondary" />}
              </button>
              <button
                onClick={handleClose}
                className="w-7 h-7 rounded-lg hover:bg-dark-600 flex items-center justify-center transition-colors"
                aria-label="Close chat"
              >
                <X className="w-4 h-4 text-secondary" />
              </button>
            </div>
          </div>

          {!minimised && (
            <>
              {showFeedback ? (
                <FeedbackPanel
                  sessionId={sessionId}
                  onClose={() => { setOpen(false); setShowFeedback(false); }}
                />
              ) : (
                <>
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-1 scrollbar-thin">
                    {messages.map((msg, i) => (
                      <MessageBubble key={i} message={msg} />
                    ))}

                    {loading && (
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-7 h-7 rounded-full bg-brand-500/20 border border-brand-500/30 flex items-center justify-center flex-shrink-0">
                          <MessageCircle className="w-3.5 h-3.5 text-brand-400" />
                        </div>
                        <div className="bg-dark-600 border border-dark-500 px-4 py-2.5 rounded-2xl rounded-bl-sm">
                          <div className="flex gap-1 items-center">
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce bounce-delay-0" />
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce bounce-delay-150" />
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce bounce-delay-300" />
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Suggested questions (page-aware; generic fallback). Only
                      while the conversation is fresh (no user messages yet). */}
                  {messages.filter(m => m.role === 'user').length === 0 && (
                    <div className="px-4 pb-2 flex flex-wrap gap-1.5">
                      {(pageSuggestions || FAQ_SUGGESTIONS).slice(0, 4).map(s => (
                        <button
                          key={s.label}
                          onClick={() => sendMessage(s.text)}
                          disabled={loading}
                          className="px-2.5 py-1 bg-dark-700 hover:bg-dark-600 border border-dark-500 hover:border-dark-400 text-secondary hover:text-brand-ink text-label rounded-full transition-all"
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Input */}
                  <div className="flex items-end gap-2 p-3 border-t border-dark-600 flex-shrink-0">
                    <textarea
                      ref={inputRef}
                      value={input}
                      onChange={e => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Ask anything about your memorial video..."
                      rows={1}
                      className="flex-1 px-3 py-2 bg-dark-700 border border-dark-500 rounded-xl text-brand-ink text-body placeholder-brand-taupe focus:outline-none focus:border-brand-500 resize-none max-h-24 overflow-y-auto chat-input"
                      disabled={loading || !sessionId}
                    />
                    <button
                      onClick={() => sendMessage()}
                      disabled={loading || !input.trim() || !sessionId}
                      className="w-10 h-10 bg-brand-500 hover:bg-brand-400 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl flex items-center justify-center flex-shrink-0 transition-colors"
                      aria-label="Send message"
                    >
                      {loading ? (
                        <Loader className="w-4 h-4 text-dark-900 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 text-dark-900" />
                      )}
                    </button>
                  </div>

                  {/* Help hint */}
                  <div className="px-4 pb-3 flex items-center gap-1 text-secondary text-label">
                    <HelpCircle className="w-3 h-3" />
                    <span>Ask about templates, music, payments, or sharing.</span>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
